import cx from "classnames";
import React, { memo } from "react";

import { Block, DataPointValue, NonGroupTaxonomyEntry, State, TaxonomyEntryType } from "../../types";

import { ConfidenceState } from "../../state/util";
import { AddButton } from "../AddButton";
import { DuplicateButton } from "../DuplicateButton";
import { RemoveButton } from "../RemoveButton";
import { WarningButton } from "../WarningButton";
import { BooleanInput } from "./BooleanInput";
import { InputComponentProps } from "./common";
import { DateTimeInput } from "./DateTimeInput";
import { EnumInput } from "./EnumInput";
import { NumberInput } from "./NumberInput";
import { PointerInput } from "./PointerInput";
import { StringInput } from "./StringInput";

type Props<T extends NonGroupTaxonomyEntry, V> = Readonly<{
  active: boolean;
  confidenceState: ConfidenceState;
  dataPoint: T;
  disableAdd: boolean;
  disableRemove: boolean;
  id: string;
  label: string;
  onAddValue: () => void;
  onDuplicateValue: () => void;
  onFocus: InputComponentProps<T, V>["onFocus"];
  onRemove: (key: string, disableRemove: boolean) => void;
  onScrollToBlock: (key: string) => void;
  onUpdate: InputComponentProps<T, V>["onUpdate"];
  focusWarningsTab: () => void;
  hasWarning: boolean;
  review: boolean;
  showLabel: boolean;
  showMultiple: boolean;
  value: DataPointValue<V>;
  block:  Block;
}>;

const AnswerComponent = <T extends NonGroupTaxonomyEntry, V>(props: Props<T, V>) => {
  const {
    active,
    confidenceState,
    dataPoint,
    disableAdd,
    disableRemove,
    id,
    label,
    onAddValue,
    onDuplicateValue,
    onFocus,
    onRemove,
    onScrollToBlock,
    onUpdate,
    focusWarningsTab,
    hasWarning,
    review,
    showLabel,
    showMultiple,
    value,
    block
  } = props;
  let inputComponent = null;
  const commonProps: InputComponentProps<any, any> = {
    active,
    dataPoint,
    id,
    invalid: false,
    onFocus,
    onUpdate,
    showAlerts: false,
    value
  };
  switch (dataPoint.type) {
    case TaxonomyEntryType.Boolean:
      inputComponent = <BooleanInput {...commonProps} />;
      break;
    case TaxonomyEntryType.Date:
      inputComponent = <DateTimeInput {...commonProps} />;
      break;
    case TaxonomyEntryType.DateTime:
      inputComponent = <DateTimeInput {...commonProps} />;
      break;
    case TaxonomyEntryType.Enum:
      inputComponent = <EnumInput {...commonProps} />;
      break;
    case TaxonomyEntryType.Number:
      inputComponent = <NumberInput {...{...commonProps, block }} />;
      break;
    case TaxonomyEntryType.Pointer:
      inputComponent = <PointerInput {...commonProps} />;
      break;
    case TaxonomyEntryType.Selection:
      inputComponent = <StringInput {...{...commonProps, block }} />;
      break;
    case TaxonomyEntryType.String:
      inputComponent = <StringInput {...{...commonProps, block }} />;
      break;
    case TaxonomyEntryType.Time:
      inputComponent = <DateTimeInput {...commonProps} />;
      break;
    default:
      throw new Error(`Unhandled taxonomy entry type: "${dataPoint.type}."`);
  }

  const rowClasses = cx("grouped-section", {
    active,
    "high-confidence": confidenceState === ConfidenceState.High,
    "human-edited": confidenceState === ConfidenceState.Manual && value.valueRaw.trim().length > 0,
    "human-edited-no-value": confidenceState === ConfidenceState.Manual && (value.idRef === null || value.valueRaw.trim().length === 0),
    "low-confidence": confidenceState === ConfidenceState.Low,
  });
  let title = value.valueKey;
  let confidence = '';


  if (value.idRef || value?.valueRaw?.length > 0) {
    confidence = `${Math.round(value.confidence * 100)}%`
    title = `${title} ${confidence}`;
  }
  return (
    <div className={rowClasses} id={`question-${value.valueKey}`} title={title}
      onClick={() => {
        onFocus(id);
      }}
    >
      <div className={cx("grouped-row-label align-items-center d-flex flex-row justify-content-between")}>
          {showLabel && (!!label ? label.toUpperCase() : value.valueKey)}         
        <div>
          {confidenceState === ConfidenceState.Low && 
            <div className="float-left underline" role="button"
              onClick={() => {
                onUpdate(value.valueKey, value.value, value.valueRaw);
              }}
            >
              <u>ACCEPT</u>
            </div> 
          } 
          {confidenceState !== ConfidenceState.Manual && 
            <div className="float-right">&nbsp;&nbsp;{confidence}</div>
          }        
          {confidenceState === ConfidenceState.Manual && value.valueRaw.trim().length > 0 && 
            <div>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 5.5C7.10457 5.5 8 4.60457 8 3.5C8 2.39543 7.10457 1.5 6 1.5C4.89543 1.5 4 2.39543 4 3.5C4 4.60457 4.89543 5.5 6 5.5Z" stroke="black" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 10.5V8.5C2 7.9477 2.44771 7.5 3 7.5H9C9.5523 7.5 10 7.9477 10 8.5V10.5" stroke="black" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>  
            </div>
          }              
        </div>
      </div>
      <div className="grouped-row">
        <div className={cx("grouped-column has-ref", {
          active,
          "high-confidence": value.idRef !== null && confidenceState === ConfidenceState.High,
          "human-edited": value.idRef !== null && confidenceState === ConfidenceState.Manual && value.valueRaw.trim().length > 0,
          "human-edited-no-value": confidenceState === ConfidenceState.Manual && (value.idRef === null || value.valueRaw.trim().length === 0),
          "low-confidence": value.idRef !== null && confidenceState === ConfidenceState.Low,
        })} style={{ height: "37px" }} ></div>
        <div className={cx("grouped-column column-input", { "full-width": true })}>
          {inputComponent}
        </div>
        {showMultiple && (
          <>
            <div className="grouped-column column-add">
              {!disableAdd && <AddButton onClick={onAddValue} />}
            </div>
            <div className="grouped-column column-duplicate">
             {!disableAdd && <DuplicateButton onClick={onDuplicateValue} />}
            </div>
          </>
        )}
        {(hasWarning && <div className="grouped-column column-warning">
          <WarningButton
            clear={disableRemove}
            onClick={() => {
              focusWarningsTab();
            }}
            review={review}
          />
        </div>)}
        <div className="grouped-column column-remove">
          <RemoveButton
            clear={disableRemove}
            onClick={() => {
              onRemove(id, disableRemove);
            }}
            review={review}
          />
        </div>
      </div>
    </div>
  );
};
AnswerComponent.displayName = "Answers";
export const Answer = memo(AnswerComponent);
